"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, ChevronLeft } from "lucide-react";
import Link from "next/link";

const MIN_VOLUME = 5_000;
const MAX_VOLUME = 50_000;
const DEFAULT_VOLUME = 20_000;
const EFFECTIVE_CARD_RATE = 0.026; // 2.6%
const CARD_MIX = 0.65; // 65% of payments are card on average

function useOdometer(target: number, durationMs = 700) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = display;
    const delta = target - start;
    const startedAt = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / durationMs);
      // Spring-like ease: cubic-bezier(0.21, 0.47, 0.32, 0.98)
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(start + delta * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return display;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export default function ZeroFeesHero() {
  const { t } = useLang();
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const cardVolume = volume * CARD_MIX;
  const typicalFees = cardVolume * EFFECTIVE_CARD_RATE;
  const feesFromMargin = 0; // dual pricing: the card price is designed to cover the processing cost
  const savings = typicalFees - feesFromMargin;

  const typicalDisplay = useOdometer(typicalFees);
  const savingsDisplay = useOdometer(savings);
  const settleControls = useAnimation();

  // Settle pulse on release (when user stops dragging the slider)
  const handleRelease = () => {
    settleControls.start({
      scale: [1, 1.04, 1],
      transition: { duration: 0.45, ease: [0.21, 0.47, 0.32, 0.98] },
    });
  };

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-[#FAFAF8]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-zero-fees.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAF8]/80 pointer-events-none" />
      {/* Money-green ambient glow */}
      <div className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,200,150,0.10) 0%, transparent 70%)" }} />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,200,150,0.06) 0%, transparent 70%)" }} />

      <div className="relative max-w-[1200px] mx-auto">
        {/* Back link */}
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#00A876" }}
        >
          {t("Dual pricing · The fee, redirected", "Dual pricing · El fee, redireccionado")}
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("The fee.", "El fee.")}
          <br />
          <span style={{
            background: "linear-gradient(135deg, #00C896 0%, #00A876 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}>
            {t("Now their fee.", "Ahora es el suyo.")}
          </span>
        </motion.h1>

        {/* Subhead */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[640px] mx-auto leading-relaxed mb-16"
        >
          {t(
            "The customer chooses cash or card. Card payers cover the processing cost, so it doesn't come out of your margin.",
            "El cliente elige efectivo o tarjeta. Quien paga con tarjeta cubre el costo de procesamiento, así no sale de tu margen."
          )}
        </motion.p>

        {/* Calculator card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[920px] mx-auto"
        >
          <div className="absolute -inset-4 rounded-[40px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(0,200,150,0.12) 0%, transparent 65%)" }} />

          <motion.div
            animate={settleControls}
            className="relative bg-white rounded-[32px] border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.06)] p-8 md:p-12"
          >
            {/* Card label */}
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
              {t("Your monthly card volume", "Tu volumen mensual con tarjeta")}
            </div>

            {/* Slider with display */}
            <div className="flex items-end justify-between mb-3">
              <div className="text-[42px] md:text-[56px] font-black tracking-tight text-redy-charcoal leading-none">
                {formatCurrency(volume)}
              </div>
              <div className="text-[12px] font-bold text-redy-secondary mb-2">
                /{t("month", "mes")}
              </div>
            </div>

            <input
              type="range"
              min={MIN_VOLUME}
              max={MAX_VOLUME}
              step={1000}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              onMouseUp={handleRelease}
              onTouchEnd={handleRelease}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#00C896] to-[#00A876]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#00C896] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
            />

            <div className="flex justify-between text-[11px] font-bold text-redy-secondary/50 mt-2">
              <span>{formatCurrency(MIN_VOLUME)}</span>
              <span>{formatCurrency(MAX_VOLUME)}+</span>
            </div>

            {/* Divider */}
            <div className="h-px bg-black/5 my-10" />

            {/* Two columns: typical card fees vs Redy dual pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mb-8">
              {/* Typical card fees column */}
              <div className="text-left">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
                  {t("A typical 2.6% card rate costs", "Una tasa típica de 2.6% cuesta")}
                </div>
                <div className="text-[40px] md:text-[52px] font-black tracking-tight text-redy-charcoal/40 leading-none tabular-nums">
                  −{formatCurrency(typicalDisplay)}
                </div>
                <div className="text-[12px] font-medium text-redy-secondary/70 mt-2">
                  {t("Out of your bank, every month.", "Salen de tu banco, cada mes.")}
                </div>
              </div>

              {/* Redy column */}
              <div className="text-left relative">
                <div className="absolute -inset-x-4 -inset-y-3 rounded-[20px] pointer-events-none"
                  style={{ background: "radial-gradient(ellipse, rgba(0,200,150,0.08) 0%, transparent 70%)" }} />
                <div className="relative">
                  <div className="text-[10px] font-black tracking-widest uppercase mb-3" style={{ color: "#00A876" }}>
                    {t("Out of your margin with Redy", "De tu margen con Redy")}
                  </div>
                  <div className="text-[40px] md:text-[52px] font-black tracking-tight leading-none tabular-nums" style={{ color: "#00A876" }}>
                    $0
                  </div>
                  <div className="text-[12px] font-medium mt-2" style={{ color: "#00A876", opacity: 0.7 }}>
                    {t("The card price covers it.", "El precio con tarjeta lo cubre.")}
                  </div>
                </div>
              </div>
            </div>

            {/* Savings hero number */}
            <div className="bg-gradient-to-br from-[#00C896] to-[#00A876] rounded-[24px] px-6 py-6 md:px-8 md:py-8 text-white shadow-lg shadow-[#00C896]/20">
              <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
                <div>
                  <div className="text-[11px] font-black tracking-widest uppercase text-white/70 mb-2">
                    {t("Card fees covered (estimate)", "Fees de tarjeta cubiertos (estimado)")}
                  </div>
                  <div className="text-[48px] md:text-[64px] font-black leading-none tabular-nums">
                    +{formatCurrency(savingsDisplay)}
                  </div>
                  <div className="text-[13px] font-bold text-white/85 mt-1">
                    /{t("month", "mes")} · {formatCurrency(savingsDisplay * 12)}/{t("year", "año")}
                  </div>
                </div>
                <Link
                  href="/signup"
                  className="inline-flex items-center gap-2 bg-white text-redy-charcoal font-bold text-[14px] px-6 py-3.5 rounded-full hover:scale-[1.03] transition-transform shadow-lg whitespace-nowrap"
                >
                  {t("Start keeping it", "Empieza a quedártelo")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Trust micro-line */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-8 text-[12px] font-medium text-redy-secondary/60"
        >
          {t(
            "Based on a 2.6% effective card rate and 65% card mix — your numbers may vary.",
            "Basado en 2.6% efectivo de tarjeta y 65% de mix con tarjeta — tus números pueden variar."
          )}
        </motion.div>
      </div>
    </section>
  );
}
